/* eslint-disable no-underscore-dangle */
import get from 'lodash/get';
import dayjs from 'utils/dayjs';
import { createIntl, createIntlCache } from 'react-intl';
import English from '../../lang/en.json';
import French from '../../lang/fr.json';
import { COUNTRY } from '../environment';

const timezoneKey = 'current-timezone';

const intl = () => {
  const cache = createIntlCache();
  const locale = localStorage.getItem('locale') || 'en';
  const messages = locale == 'en' ? English : French;

  return createIntl({ locale, messages }, cache);
};

const { formatMessage } = intl();

export const canadianTimezones = [
  {
    label: formatMessage({ defaultMessage: 'Hawaii-Aleutian Time' }),
    value: 'Pacific/Honolulu',
  },
  {
    label: formatMessage({ defaultMessage: 'Alaska Time' }),
    value: 'America/Juneau',
  },
  {
    label: formatMessage({
      defaultMessage: 'Pacific Time (Vancouver)',
    }),
    value: 'America/Vancouver',
  },
  {
    label: formatMessage({ defaultMessage: 'Mountain Time (Edmonton)' }),
    value: 'America/Edmonton',
  },
  {
    label: formatMessage({ defaultMessage: 'Central Time (Winnipeg)' }),
    value: 'America/Winnipeg',
  },
  {
    label: formatMessage({ defaultMessage: 'Eastern Time (New York/Toronto)' }),
    value: 'America/Toronto',
  },
  {
    label: formatMessage({ defaultMessage: 'Atlantic Time (Halifax)' }),
    value: 'America/Halifax',
  },
  {
    label: formatMessage({ defaultMessage: 'Newfoundland Time' }),
    value: 'America/St_Johns',
  },
  {
    label: formatMessage({
      defaultMessage: 'Saskatchewan (Central Standard Time)',
    }),
    value: 'America/Swift_Current',
  },
  {
    label: formatMessage({ defaultMessage: 'Yukon (Mountain Standard Time)' }),
    value: 'MST',
  },
  {
    label: formatMessage({
      defaultMessage: 'Pickle Lake/Atikokan (Eastern Standard Time)',
    }),
    value: 'EST',
  },
];

export const usTimezones = [
  {
    label: formatMessage({ defaultMessage: 'Hawaii-Aleutian Time' }),
    value: 'Pacific/Honolulu',
  },
  {
    label: formatMessage({ defaultMessage: 'Alaska Time' }),
    value: 'America/Juneau',
  },
  {
    label: formatMessage({ defaultMessage: 'Pacific Time (Los Angeles)' }),
    value: 'America/Los_Angeles',
  },
  {
    label: formatMessage({ defaultMessage: 'Mountain Time (Denver)' }),
    value: 'America/Denver',
  },
  {
    label: formatMessage({ defaultMessage: 'Central Time (Chicago)' }),
    value: 'America/Chicago',
  },
  {
    label: formatMessage({ defaultMessage: 'Eastern Time (New York/Toronto)' }),
    value: 'America/Toronto',
  },
  {
    label: formatMessage({ defaultMessage: 'Atlantic Time (Halifax)' }),
    value: 'America/Halifax',
  },
  {
    label: formatMessage({ defaultMessage: 'Newfoundland Time' }),
    value: 'America/St_Johns',
  },
  {
    label: formatMessage({
      defaultMessage: 'Saskatchewan (Central Standard Time)',
    }),
    value: 'America/Swift_Current',
  },
  {
    label: formatMessage({ defaultMessage: 'Yukon (Mountain Standard Time)' }),
    value: 'MST',
  },
  {
    label: formatMessage({
      defaultMessage: 'Pickle Lake/Atikokan (Eastern Standard Time)',
    }),
    value: 'EST',
  },
];

export const TIMEZONES = COUNTRY === 'US' ? usTimezones : canadianTimezones;

// The stored user preference is always primary. The browser's IANA timezone
// name is used only when the user does not have a stored timezone.
export const getPreferenceTz = () =>
  localStorage.getItem(timezoneKey) || dayjs.tz.guess();

export const fromInstant = value => dayjs(value).tz(getPreferenceTz());

export const nowInPreferenceTz = () => fromInstant(Date.now());

export const getTimezoneLabel = value => {
  const timezone = TIMEZONES.find(option => option.value === value);
  return timezone ? timezone.label : value;
};

export const setDefaultTimezone = user => {
  const timezone = get(user, 'preference.timezone', dayjs.tz.guess());
  const currentTimezone = localStorage.getItem(timezoneKey);

  if (timezone !== currentTimezone) {
    localStorage.setItem(timezoneKey, timezone);
  }

  // Unlike moment.tz.setDefault(), this only affects dayjs.tz() calls — bare
  // dayjs() still uses browser-local time. Use fromInstant(value) or
  // nowInPreferenceTz() wherever preference-timezone output is needed.
  dayjs.tz.setDefault(timezone);
};

export const useDefaultTimezone = () => {
  const timezone = localStorage.getItem(timezoneKey) || dayjs.tz.guess();

  dayjs.tz.setDefault(timezone);
};
